import type { GfaServiceConfigProviderV1 } from './feature-service-types';

export interface AcsResult {
  caeDataRequired: boolean;
}

export interface FetchCaeEnforcementInput {
  gfaServiceConfigProvider?: GfaServiceConfigProviderV1;
}

const ACS_TIMEOUT_MS = 5000;
const DEFAULT_ACS_APPLICATION = 'market-context';
const CONFIG_KEY_VARIATIONS = ['cae_data_required', 'caeDataRequired'] as const;

const inflightRequests = new Map<string, Promise<AcsResult>>();

const buildAcsUrl = (gfaServiceConfigProvider?: GfaServiceConfigProviderV1): string | null => {
  const gfa = gfaServiceConfigProvider?.gfa;
  if (!gfa) {
    return null;
  }

  const acsConfig = gfa.acs;

  if (!acsConfig || !acsConfig.origin || !acsConfig.path) {
    return null;
  }

  const tenantValue = acsConfig.customConfig?.tenant;
  const tenant = typeof tenantValue === 'string' ? tenantValue : '';

  const appValue = acsConfig.customConfig?.application;
  const application = typeof appValue === 'string' ? appValue : DEFAULT_ACS_APPLICATION;

  const params = new URLSearchParams();
  params.set('tenant', tenant);
  params.set('application', application);

  return `${acsConfig.origin}${acsConfig.path}?${params.toString()}`;
};

const parseConfiguration = (configuration: unknown): Record<string, unknown> | null => {
  if (typeof configuration === 'string') {
    try {
      const parsed = JSON.parse(configuration) as unknown;
      return typeof parsed === 'object' && parsed !== null
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  if (typeof configuration === 'object' && configuration !== null) {
    return configuration as Record<string, unknown>;
  }

  return null;
};

const extractCaeDataRequiredFlag = (configuration: Record<string, unknown>): boolean => {
  for (const keyVariation of CONFIG_KEY_VARIATIONS) {
    const value = configuration[keyVariation];

    if (value === true) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim().toLowerCase() === 'true';
    }
  }

  return false;
};

const fetchCaeData = async (url: string): Promise<AcsResult> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, ACS_TIMEOUT_MS);

  try {
    const response = await fetch(url, { signal: controller.signal });

    if (!response.ok) {
      return { caeDataRequired: false };
    }

    const data = (await response.json()) as { configuration?: unknown };
    const parsedConfig = parseConfiguration(data.configuration);

    if (parsedConfig === null) {
      return { caeDataRequired: false };
    }

    const caeDataRequired = extractCaeDataRequiredFlag(parsedConfig);
    return { caeDataRequired };
  } catch {
    return { caeDataRequired: false };
  } finally {
    clearTimeout(timeoutId);
  }
};

export const fetchCaeEnforcement = async ({
  gfaServiceConfigProvider,
}: FetchCaeEnforcementInput): Promise<AcsResult> => {
  const defaultAcsUrl = process.env.MARKET_CONTEXT_SERVICE_ACS_URL;
  const url = buildAcsUrl(gfaServiceConfigProvider) ?? defaultAcsUrl;

  if (!url) {
    return { caeDataRequired: false };
  }

  const cachedRequest = inflightRequests.get(url);
  if (cachedRequest) {
    return cachedRequest;
  }

  const promise = fetchCaeData(url).finally(() => {
    inflightRequests.delete(url);
  });

  inflightRequests.set(url, promise);
  return promise;
};
